/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx, keyframes } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef } from 'react';

import $ from 'jquery';

import Imgix from 'js/components/Imgix';

import { Grid, PageGridContainer, breakpoints } from '@coursera/cds-core';

import CTAContentful from 'bundles/coursera-plus-landing-page/components/ctaContentful/CTAContentful';
import { CTA_LOCATIONS } from 'bundles/coursera-plus-landing-page/contentful/constants';
import { useLandingPageExtended } from 'bundles/coursera-plus-landing-page/contentfulData/useLandingPageData';
import { logo } from 'bundles/coursera-plus/utils/courseraPlusUtils';

import _t from 'i18n!nls/coursera-plus-landing-page';

const animateIn = keyframes`
  0% { 
    opacity: 0;
    transform: translateY(-200px);
  }
  100% {
    opacity: 1;
    transform: translateY(0px);
  }
`;

const mobileAnimation = keyframes`
  0% { 
    opacity: 0;
    transform: translateY(200px);
  }
  100% {
    opacity: 1;
    transform: translateY(0px);
  }
`;

type Props = {
  heroRef: React.RefObject<HTMLDivElement>;
  customCss?: SerializedStyles;
};

const styles = {
  desktopContainer: css`
    position: fixed;
    background: var(--cds-color-white-0);
    width: 100%;
    opacity: 0;
    transform: translateY(-200px);
    z-index: 3000;
    top: 0;
    display: none;
    box-shadow: 0 2px 12px rgb(0 0 0 / 24%);
    animation: 0.5s ease-in-out 1 normal forwards running ${animateIn};
  `,
  slideInDesktop: css`
    display: block;
    animation: 0.5s ease-in-out 1 normal forwards running ${animateIn};
  `,
  mobileContainer: css`
    /* 1000 z-index is used on flyout menu on mobile. This shouldn't cover that. */
    z-index: 999;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    transform: translateY(200px);
    background: var(--cds-color-white-0);
    padding: var(--cds-spacing-200);
    box-shadow: 0 0 4px rgb(0 0 0 / 15%);
    display: none;

    ${breakpoints.down('sm')} {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
  `,
  slideInMobile: css`
    ${breakpoints.down('sm')} {
      display: block;
      animation: 0.5s ease-in-out 1 normal forwards running ${mobileAnimation};
    }
  `,
  header: css`
    padding: var(--cds-spacing-150) 0;
    display: flex;
    gap: var(--cds-spacing-400);
    justify-content: space-between;
    align-items: center;
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
  titleContainer: css`
    display: flex;
    gap: var(--cds-spacing-300);
    align-items: center;
    flex: 1;
    min-width: 0;
  `,
  title: css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
};

const FixedPageNavigation = ({ heroRef, customCss }: Props) => {
  const extendedLandingPage = useLandingPageExtended();
  const [displayNav, setDisplayNav] = React.useState(false);
  const navRef = useRef<HTMLDivElement | null>(null);

  const { isCourseraLiteContentfulPage } = extendedLandingPage;
  const ctaPieces = extendedLandingPage.landingPage.heroCtaPartsCollection?.items.filter((item) =>
    item?.presentation?.toLowerCase().includes('button')
  );
  const title = isCourseraLiteContentfulPage ? _t('Coursera Lite') : _t('Coursera Plus');
  const cplusLogo = (
    <Imgix src={isCourseraLiteContentfulPage ? logo.LITE_FULL_BLUE_HD : logo.PLUS_FULL_BLUE} alt={title} height={20} />
  );
  const cta = <CTAContentful ctaPieces={ctaPieces} ctaLocation={CTA_LOCATIONS.sticky} />;

  useEffect(() => {
    const onScroll = () => {
      if (!heroRef.current) return;
      const heroBottom = heroRef.current.getBoundingClientRect().bottom;
      if (heroBottom < 0) setDisplayNav(true);
      else setDisplayNav(false);
    };
    window.addEventListener('scroll', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [heroRef]);

  useEffect(() => {
    // prevent the help center button from overlapping the sticky footer
    const helpButton = $('button.rc-Help');
    helpButton.css('z-index', displayNav ? '998' : '1000');
  }, [displayNav]);

  return (
    <div css={customCss}>
      <div
        data-testid="fixed-page-navigation"
        css={[styles.desktopContainer, displayNav ? styles.slideInDesktop : undefined]}
        ref={navRef}
      >
        <PageGridContainer>
          <Grid container item xs={12}>
            <Grid item xs={12}>
              <div css={styles.header}>
                <div css={styles.titleContainer}>{cplusLogo}</div>
                <div>{cta}</div>
              </div>
            </Grid>
          </Grid>
        </PageGridContainer>
      </div>
      <div
        data-testid="mobile-cta-module"
        css={[styles.mobileContainer, displayNav ? styles.slideInMobile : undefined]}
      >
        {cta}
      </div>
    </div>
  );
};

export default FixedPageNavigation;
